import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { authService } from '../../../lib/api/apiService';
import { useTranslation } from 'react-i18next';

const createForgotPasswordSchema = (t) =>
    z.object({
        email: z
            .string()
            .trim()
            .min(1, t('forgot_password.validation.email_required'))
            .email(t('forgot_password.validation.email_invalid')),
    });

const ForgotPassword = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const schema = useMemo(() => createForgotPasswordSchema(t), [t]);
    const trimmedEmail = useMemo(() => String(email ?? '').trim(), [email]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        const result = schema.safeParse({ email: trimmedEmail });
        if (!result.success) {
            const next = {};
            result.error.issues.forEach((err) => {
                if (err.path[0]) next[err.path[0]] = err.message;
            });
            setFieldErrors(next);
            return;
        }

        setFieldErrors({});
        setLoading(true);

        try {
            // Appel de l'API pour demander la réinitialisation
            const result = await authService.forgotPassword(trimmedEmail);
            
            if (result.success) {
                setSuccess(true);
            } else {
                setError(result.message || t('forgot_password.errors.send_error'));
            }
        } catch (error) {
            console.error('Erreur lors de la demande de réinitialisation:', error);
            setError(t('forgot_password.errors.unexpected_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: 'url("/images/auth/background-image.png")'
                }}>
            </div>

            <div className="relative z-10 flex w-full max-w-6xl justify-between">
                {/* Forgot Password Form - Left Side */}
                <div className="w-full lg:w-1/2 flex items-center justify-center">
                    <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md">
                        <div className="flex justify-center">
                            <Link to="/" aria-label={t('navbar.home')}>
                                <img src="/images/logo.png" alt="logo" className="w-auto h-14 cursor-pointer" />
                            </Link>
                        </div>

                        {/* Welcome Message */}
                        <div className="text-center mb-8 mt-4">
                            <h2 className="text-xl font-bold text-gray-800 mb-2">
                                {t('forgot_password.title')}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                {t('forgot_password.description')}
                            </p>
                        </div>

                        {/* Success Message */}
                        {success && (
                            <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('forgot_password.success.email_sent')}
                                </div>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        {/* Forgot Password Form */}
                        {!success ? (
                            <form onSubmit={handleSubmit} noValidate className="space-y-4">
                                {/* Email Field */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('forgot_password.email_label')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setFieldErrors((prev) => {
                                                if (!prev.email) return prev;
                                                const next = { ...prev };
                                                delete next.email;
                                                return next;
                                            });
                                        }}
                                        placeholder={t('forgot_password.email_placeholder')}
                                        autoComplete="email"
                                        aria-invalid={Boolean(fieldErrors.email)}
                                        aria-describedby={fieldErrors.email ? 'forgot-password-email-error' : undefined}
                                        className={`text-sm w-full px-4 py-3 border rounded-full focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${
                                            fieldErrors.email
                                                ? 'border-red-500 focus:ring-red-200'
                                                : 'border-gray-300 focus:ring-orange-light'
                                        }`}
                                    />
                                    {fieldErrors.email && (
                                        <p id="forgot-password-email-error" className="mt-2 text-sm text-red-600" role="alert">
                                            {fieldErrors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full text-white text-base py-2 px-6 rounded-full transform transition-all duration-200 shadow-lg ${
                                        loading 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-orange-hover hover:bg-orange-600 hover:scale-105'
                                    }`}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('forgot_password.sending')}
                                        </div>
                                    ) : (
                                        t('forgot_password.send_button')
                                    )}
                                </button>
                            </form>
                        ) : (
                            /* Success State */
                            <div className="text-center space-y-4">
                                <div className="text-green-600">
                                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <p className="text-gray-600 text-sm">
                                    {t('forgot_password.success.message')}
                                </p>
                                <button
                                    onClick={() => {
                                        setSuccess(false);
                                        setEmail('');
                                        setError('');
                                    }}
                                    className="text-sm text-orange-light hover:text-orange-hover font-medium transition-colors"
                                >
                                    {t('forgot_password.success.resend')}
                                </button>
                            </div>
                        )}

                        {/* Back to Login */}
                        <div className="text-center mt-6">
                            <Link
                                to="/vendor/login"
                                className="text-sm text-gray-600 hover:text-orange-light font-medium transition-colors"
                            >
                                {t('forgot_password.back_to_login')}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Marketing Message - Right Side */}
                <div className="hidden lg:flex lg:w-1/3 items-end justify-end">
                    <div className="text-white max-w-md text-center">
                        <h2 className="text-xl font-medium text-right mb-4">
                            {t('forgot_password.marketing.title')}
                        </h2>
                        <p className="text-sm text-gray-200 text-right leading-relaxed">
                            {t('forgot_password.marketing.description')}
                        </p>
                        
                        <div className="flex justify-end space-x-1 mt-6">
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
